import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HelpTooltip from '../common/HelpTooltip';

interface UserMenuProps {
  onClose: () => void;
}

type MenuItem = {
  label: string;
  path: string;
  hint?: string;
  roles?: Array<'BUYER' | 'SELLER' | 'ADMIN'>;
  icon: React.ReactNode;
};

const UserMenu: React.FC<UserMenuProps> = ({ onClose }) => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    onClose();
  };

  const menuItems: MenuItem[] = [
    {
      label: 'Panel Admin',
      path: '/admin/dashboard',
      roles: ['ADMIN'],
      hint: 'Métricas y gestión global de la plataforma.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      label: 'Panel vendedor',
      path: '/dashboard',
      roles: ['SELLER'],
      hint: 'Resumen de ventas, ofertas e inventario.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
        </svg>
      ),
    },
    {
      label: 'Publicar entradas',
      path: '/seller/inventory',
      roles: ['SELLER'],
      hint: 'Elige un partido y publica tus entradas.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
    },
    {
      label: 'Pedidos de compradores',
      path: '/seller/requests',
      roles: ['SELLER'],
      hint: 'Solicitudes activas a las que puedes responder.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      label: 'Mis ventas',
      path: '/seller/sales',
      roles: ['SELLER'],
      hint: 'Historial de ventas completadas.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: 'Mi cuenta',
      path: user?.role === 'BUYER' ? '/account' : '/dashboard',
      roles: ['BUYER'],
      hint: 'Pujas, solicitudes y compras.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
        </svg>
      ),
    },
    {
      label: 'Mi perfil',
      path: '/profile',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      label: 'Mis solicitudes',
      path: '/requests',
      roles: ['BUYER'],
      hint: 'Solicitudes que publicaste para recibir ofertas.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      label: 'Mis ofertas',
      path: user?.role === 'SELLER' ? '/seller/offers' : '/offers',
      roles: ['BUYER', 'SELLER'],
      hint: user?.role === 'SELLER' ? 'Ofertas que enviaste a compradores.' : 'Ofertas que recibiste de vendedores.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
    },
    {
      label: 'Mis pagos',
      path: '/payments',
      hint: 'Historial de pagos y transacciones.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      label: 'Ayuda',
      path: '/ayuda',
      hint: 'Guías y preguntas frecuentes.',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const visibleItems = menuItems.filter((item) => {
    if (!item.roles) return true;
    return user?.role && item.roles.includes(user.role);
  });

  return (
    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-surface-200 py-2 z-50 animate-scaleIn">
      <div className="px-4 py-3 border-b border-surface-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-accent-400 to-accent-600 rounded-full flex items-center justify-center">
            <span className="text-white font-medium">
              {user?.firstName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-surface-900 truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-surface-500 truncate">{user?.email}</p>
            {user?.role && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium mt-1 ring-1 ring-inset ${
                user.role === 'ADMIN'
                  ? 'bg-primary-50 text-primary-700 ring-primary-200'
                  : user.role === 'SELLER'
                  ? 'bg-accent-50 text-accent-700 ring-accent-200'
                  : 'bg-gold-50 text-gold-800 ring-gold-200'
              }`}>
                {user.role === 'ADMIN' ? 'Administrador' :
                 user.role === 'SELLER' ? 'Vendedor' : 'Comprador'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="py-2">
        {visibleItems.map((item) => (
          <Link
            key={`${item.path}-${item.label}`}
            to={item.path}
            onClick={onClose}
            className="flex items-center justify-between px-4 py-2 text-sm text-surface-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-200 group"
          >
            <span className="flex items-center min-w-0">
              <span className="mr-3 text-surface-400">{item.icon}</span>
              <span className="truncate">{item.label}</span>
            </span>
            {item.hint && (
              <HelpTooltip content={item.hint} side="bottom" className="opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </Link>
        ))}
      </div>

      <div className="border-t border-surface-200 py-2">
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-4 py-2 text-sm text-surface-700 hover:bg-danger-50 hover:text-danger-600 transition-colors duration-200"
        >
          <span className="mr-3 text-surface-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </span>
          Cerrar sesión
        </button>
      </div>
    </div>
  );
};

export default UserMenu;
